(() => {
  'use strict';

  const CART_ROOT = '[data-cart-items]';
  const ITEM = '[data-cart-item]';
  const INPUT = '[data-quantity-value]';
  const queued = [];
  const queuedByKey = new Map();
  let activeRequest = false;

  const number = (value, fallback = 0) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const decimals = (value) => {
    const text = String(value);
    return text.includes('.') ? text.split('.')[1].length : 0;
  };

  const endpoint = () => {
    const route = window.T4Sroutes?.cart_change_url || `${window.Shopify?.routes?.root || '/'}cart/change`;
    return route.endsWith('.js') ? route : `${route}.js`;
  };

  const sections = () => {
    const ids = ['cart_data'];
    if (document.getElementById('t4s-mini_cart')) ids.push('mini_cart');
    if (window.cartT4SectionID) ids.push(window.cartT4SectionID);
    return [...new Set(ids.filter(Boolean))];
  };

  const notice = (message) => {
    if (typeof window.T4SThemeSP?.Notices === 'function') {
      window.T4SThemeSP.Notices(message);
      return;
    }
    document.dispatchEvent(new CustomEvent('basictex:notice', { detail: { message } }));
  };

  const findInput = (key) => Array.from(document.querySelectorAll(`${CART_ROOT} ${INPUT}`))
    .find((input) => String(input.dataset.id) === String(key));

  const markBusy = (key, busy, removing = false) => {
    const input = findInput(key);
    const item = input?.closest(ITEM)
      || Array.from(document.querySelectorAll(`${CART_ROOT} [data-cart-remove]`))
        .find((button) => String(button.dataset.id) === String(key))?.closest(ITEM);
    if (!item) return;
    item.classList.toggle('bt-cart-item--busy', busy);
    item.classList.toggle('bt-cart-item--removing', busy && removing);
    if (busy) item.setAttribute('aria-busy', 'true');
    else item.removeAttribute('aria-busy');
  };

  const syncCart = (payload) => {
    if (payload?.sections && typeof window.T4SThemeSP?.Cart?.renderContents === 'function') {
      window.T4SThemeSP.Cart.renderContents(payload.sections);
    } else if (typeof window.T4SThemeSP?.Cart?.getToFetch === 'function') {
      window.T4SThemeSP.Cart.getToFetch();
    } else {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }
  };

  const requestChange = async ({ key, quantity, previous }) => {
    markBusy(key, true, quantity === 0);
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 12000);

    try {
      const response = await fetch(endpoint(), {
        method: 'POST',
        credentials: 'same-origin',
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: key,
          quantity,
          sections: sections(),
          sections_url: window.location.pathname
        })
      });
      const payload = await response.json();
      if (!response.ok || payload.status) throw new Error(payload.description || payload.message || 'No pudimos actualizar el carrito.');
      syncCart(payload);
    } catch (error) {
      const input = findInput(key);
      if (input && previous > 0) input.value = previous;
      markBusy(key, false);
      if (typeof window.T4SThemeSP?.Cart?.getToFetch === 'function') window.T4SThemeSP.Cart.getToFetch();
      notice(error.name === 'AbortError'
        ? 'La actualización tardó demasiado. Revisa tu conexión e inténtalo de nuevo.'
        : error.message || 'No pudimos actualizar el carrito. Inténtalo de nuevo.');
    } finally {
      window.clearTimeout(timer);
    }
  };

  const drain = async () => {
    if (activeRequest) return;
    activeRequest = true;
    while (queued.length) {
      const change = queued.shift();
      queuedByKey.delete(change.key);
      await requestChange(change);
    }
    activeRequest = false;
  };

  const enqueue = (change) => {
    const existing = queuedByKey.get(change.key);
    if (existing) {
      existing.quantity = change.quantity;
      return;
    }
    queued.push(change);
    queuedByKey.set(change.key, change);
    drain();
  };

  const updateFromButton = (button) => {
    const item = button.closest(ITEM);
    const input = item?.querySelector(INPUT);
    const key = input?.dataset.id || item?.querySelector('[data-cart-remove]')?.dataset.id;
    if (!input || !key) return;

    const current = number(input.value, number(input.dataset.currentQty, 1));
    const min = number(input.min, 1);
    const max = number(input.max, 9999);
    const step = Math.max(number(input.step, 1), 0.01);
    let quantity;

    if (button.matches('[data-increase-qty]')) {
      quantity = Math.min(max, current + step);
      if (quantity === current) {
        notice(`La cantidad máxima disponible es ${max}.`);
        return;
      }
    } else {
      quantity = current <= min ? 0 : Math.max(min, current - step);
    }

    input.value = quantity === 0 ? current : quantity.toFixed(decimals(step));
    enqueue({ key, quantity, previous: current });
  };

  const removeFromButton = (button) => {
    const item = button.closest(ITEM);
    const input = item?.querySelector(INPUT);
    const key = button.dataset.id || input?.dataset.id;
    if (!key) return;
    enqueue({ key, quantity: 0, previous: number(input?.value, 1) });
  };

  document.addEventListener('click', (event) => {
    const remove = event.target.closest(`${CART_ROOT} [data-cart-remove]`);
    const selector = event.target.closest(`${CART_ROOT} [data-quantity-selector]`);
    if (!remove && !selector) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (remove) removeFromButton(remove);
    else updateFromButton(selector);
  }, true);

  document.addEventListener('change', (event) => {
    const input = event.target.closest?.(`${CART_ROOT} [data-action-change]`);
    if (!input) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const previous = number(input.dataset.currentQty, 1);
    const min = number(input.min, 1);
    const max = number(input.max, 9999);
    const requested = Math.min(max, Math.max(min, number(input.value, previous)));
    input.value = requested;
    enqueue({ key: input.dataset.id, quantity: requested, previous });
  }, true);

  const initializeProducts = () => {
    if (typeof window.T4SThemeSP?._initProducts === 'function') window.T4SThemeSP._initProducts();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeProducts, { once: true });
  } else {
    initializeProducts();
  }
  document.addEventListener('shopify:section:load', initializeProducts);
})();
